import { CONTACT_STATUS } from '../constants';
import { formatCelular } from './userUtils';

/**
 * Obtiene las iniciales de un nombre de contacto
 * @param {string} nombre - Nombre completo del contacto
 * @returns {string} - Iniciales en mayúsculas (máximo 2 caracteres)
 */
export const getContactInitials = (nombre) => {
  if (!nombre) return 'SC'; // Sin Contacto
  
  return nombre
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

/**
 * Procesa contactos del backend agregando campos calculados
 * @param {Array} contacts - Lista de contactos del backend
 * @returns {Array} - Contactos procesados
 */
export const processContactsData = (contacts) => {
  if (!Array.isArray(contacts)) return [];
  
  const processed = contacts.map(contact => {
    // Determinar si tiene suscripción activa basado en diferentes campos posibles
    // El backend devuelve has_active_subscription como 1 o 0
    const hasActiveSubscription = !!(
      contact.has_active_subscription === 1 ||  // Campo principal del backend (1 = true, 0 = false)
      contact.has_active_subscription === true ||  // Por si viene como boolean
      contact.hasActiveSubscription ||  // Campo directo si existe
      contact.active_subscription ||      // Simplificado
      contact.subscription_status === 'active' || // Por estado
      (contact.subscription_id && contact.subscription_status !== 'inactive') // Si tiene ID de suscripción activa
    );

    // Determinar el estado detallado de la suscripción
    let subscriptionStatusDetail = 'none'; // 'none', 'active', 'expired', 'cancelled', 'suspended'
    let subscriptionStatusLabel = 'Sin suscripción';
    
    if (contact.subscription_status) {
      switch (contact.subscription_status) {
        case 'activa':
          subscriptionStatusDetail = 'active';
          subscriptionStatusLabel = 'Activa';
          break;
        case 'vencida':
          subscriptionStatusDetail = 'expired';
          subscriptionStatusLabel = 'Vencida';
          break;
        case 'cancelada':
          subscriptionStatusDetail = 'cancelled';
          subscriptionStatusLabel = 'Cancelada';
          break;
        case 'suspendida':
          subscriptionStatusDetail = 'suspended';
          subscriptionStatusLabel = 'Suspendida';
          break;
        default:
          subscriptionStatusDetail = 'none';
          subscriptionStatusLabel = 'Sin suscripción';
      }
    }
    
    const processedContact = {
      ...contact,
      initials: getContactInitials(contact.nombre),
      formatted_celular: formatCelular(contact.celular),
      status: contact.is_active ? CONTACT_STATUS.ACTIVE : CONTACT_STATUS.INACTIVE,
      // Campos adicionales calculados
      has_location: !!(contact.ubicacion_texto || contact.ubicacion_url),
      display_email: contact.email_ewelink || 'Sin email',
      hasActiveSubscription, // ¡CAMPO CRÍTICO AGREGADO!
      hasSubscription: hasActiveSubscription, // Alias para compatibilidad con la tabla
      // Nuevos campos para estado detallado de suscripción
      subscriptionStatusDetail,
      subscriptionStatusLabel,
      subscriptionEndDate: contact.subscription_end_date,
    };
    
    return processedContact;
  });
  
  return processed;
};

/**
 * Filtra contactos basado en término de búsqueda
 * @param {Array} contacts - Lista de contactos
 * @param {string} searchTerm - Término de búsqueda
 * @returns {Array} - Contactos filtrados
 */
export const filterContacts = (contacts, searchTerm) => {
  if (!searchTerm || searchTerm.trim() === '') {
    return contacts;
  }

  const term = searchTerm.toLowerCase().trim();
  
  return contacts.filter(contact => {
    return (
      contact.nombre?.toLowerCase().includes(term) ||
      contact.celular?.includes(term) ||
      contact.email_ewelink?.toLowerCase().includes(term) ||
      contact.ubicacion_texto?.toLowerCase().includes(term)
    );
  });
};

/**
 * Valida los datos de un contacto según el esquema del backend
 * @param {Object} contactData - Datos del contacto
 * @returns {Object} - { isValid: boolean, errors: Array }
 */
export const validateContactData = (contactData) => {
  const errors = [];
  
  // nombre es requerido
  if (!contactData.nombre?.trim()) {
    errors.push('El nombre es requerido');
  }
  
  // celular es requerido
  if (!contactData.celular?.trim()) {
    errors.push('El celular es requerido');
  } else {
    const cleanCelular = contactData.celular.replace(/[\s\-\(\)\+]/g, '');
    // Permitir 10 dígitos (México sin prefijo) o números con prefijo internacional
    if (!/^(\d{10}|\d{1,4}\d{10,12})$/.test(cleanCelular)) {
      errors.push('El celular debe tener 10 dígitos (ej: 2299288981) o incluir prefijo internacional (ej: +1 1234567890)');
    }
  }
  
  // email_ewelink es opcional pero si se proporciona debe ser válido
  if (contactData.email_ewelink && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactData.email_ewelink)) {
    errors.push('El email de eWeLink no tiene un formato válido');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Normaliza número de celular para almacenamiento
 * @param {string} celular - Número de celular
 * @returns {string} - Celular normalizado
 */
export const normalizeCelular = (celular) => {
  let cleaned = celular.replace(/[\s\-\(\)\+]/g, '');
  
  // Si el número tiene 10 dígitos, asumir que es México y agregar 52
  if (cleaned.length === 10 && /^\d{10}$/.test(cleaned)) {
    cleaned = '52' + cleaned;
  }
  
  return cleaned;
};

/**
 * Prepara datos de contacto para enviar al backend
 * @param {Object} formData - Datos del formulario
 * @returns {Object} - Datos listos para el backend
 */
export const prepareContactForBackend = (formData) => {
  return {
    nombre: formData.nombre.trim(),
    celular: normalizeCelular(formData.celular.trim()),
    email_ewelink: formData.email_ewelink?.trim() || null,
    ubicacion_texto: formData.ubicacion_texto?.trim() || null,
    ubicacion_url: formData.ubicacion_url?.trim() || null,
  };
}; 